import { c as _c } from "react/compiler-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */

import type { InferResponseTypeFromUrl } from 'api-v4-types';
import { type RefObject, useMemo } from 'react';
import useSWR, { type SWRResponse } from 'swr';
import { useUseApiCommonLogic } from '../common';
import type { BaseUseApiHook, DemoData, UseApiOptions } from '../types';
import fetchAPIv4, { type APIv4RequestInit } from './fetchAPIv4';
import type { Locale } from '@/i18n/types';
function getFetcher(fetchOptionsRef: RefObject<RequestInit | undefined>) {
  return async function fetcher<TUrl extends string>({
    url,
    args: {
      locale: locale
    }
  }: {
    url: TUrl;
    args: {
      locale: Locale;
    };
  }) {
    const response = await fetchAPIv4(url, {
      ...((fetchOptionsRef.current ?? {}) as APIv4RequestInit<'get'>),
      locale
    });
    if (!response.ok) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw await response.json();
    }
    return await response.json();
  };
}
function useApiV4(path, params, options, _demoData) {
  const $ = _c(12);
  const {
    middleware,
    skip,
    intervalMs,
    fetchOptionsRef
  } = useUseApiCommonLogic(options);
  let t0;
  if ($[0] !== fetchOptionsRef) {
    t0 = getFetcher(fetchOptionsRef);
    $[0] = fetchOptionsRef;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const fetcher = t0;
  let t1;
  if ($[2] !== params || $[3] !== path || $[4] !== skip) {
    t1 = skip ? null : {
      url: path,
      args: params
    };
    $[2] = params;
    $[3] = path;
    $[4] = skip;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const args = t1;
  let t2;
  if ($[6] !== middleware) {
    t2 = [middleware];
    $[6] = middleware;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = options?.keepPreviousData;
  let t4;
  if ($[8] !== intervalMs || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      refreshInterval: intervalMs,
      use: t2,
      keepPreviousData: t3
    };
    $[8] = intervalMs;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return useSWR(args, fetcher, t4) as Pick<SWRResponse<TSelection, Error, any>, 'data' | 'isLoading' | 'error' | 'isValidating'>;
}

/** @ts-expect-error - This is a workaround to satisfy the type checker */
const _ = useApiV4 satisfies BaseUseApiHook;
export default useApiV4;